import type { ComponentPropsOptions } from "vue";
import type { ShortcutPlatform } from "@/utils/shortcuts";
import { defineComponent } from "vue";
import { cn } from "@/utils/cn";
import { p } from "@/utils/props-builder";
import { formatShortcut } from "@/utils/shortcuts";

export type ShortcutHintVariant = "plain" | "keycap";

const shortcutHintProps = {
  keys: p.$custom<string | string[]>([String, Array]),
  platform: p.string<ShortcutPlatform>(),
  /**
   * `plain` keeps the compact inline hint; `keycap` frames the keys so they
   * read as a button affordance. Defaults to `plain` so no existing hint moves.
   */
  variant: p.string<ShortcutHintVariant>("plain"),
  class: p.string(),
} satisfies ComponentPropsOptions;

export default defineComponent({
  name: "ShortcutHint",
  props: shortcutHintProps,
  setup(props, { attrs }) {
    // Display only: a hint must never register another keyboard handler.
    return () => (
      <kbd
        data-shortcut-hint={formatShortcut(props.keys, props.platform)}
        aria-hidden="true"
        class={cn(
          "pointer-events-none inline-flex shrink-0 select-none items-center whitespace-nowrap font-mono text-[11px] font-medium",
          props.variant === "keycap"
            ? "inline-flex h-5 min-w-5 items-center justify-center rounded border border-border bg-muted px-1.5 text-xs font-medium text-muted-foreground shadow-xs"
            : "opacity-70",
          props.class,
          attrs.class ?? ""
        )}
      >
        {formatShortcut(props.keys, props.platform)}
      </kbd>
    );
  },
});
